import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'
import { cn } from '@sweetspot/scramble-ds/utils'

const SLOT_HEIGHT = 88
const SLOT_WIDTH = 140
const HEADER_HEIGHT = 70

const SlotsCalendar = ({ className, spaces, teeTimesPerSpace, currentVenue, render }) => {
  const [horizontalTimelinePosition, setHorizontalTimelinePosition] = useState(null)

  const earliestTeeTime = useMemo(() => {
    const allTimes = Object.keys(teeTimesPerSpace)
      .map((key) => teeTimesPerSpace[key])
      .flat()

    if (allTimes?.length) {
      return allTimes.sort((a, b) => a.from_unix - b.from_unix)[0]
    } else {
      return null
    }
  }, [teeTimesPerSpace])

  const latestTeeTime = useMemo(() => {
    const allTimes = Object.keys(teeTimesPerSpace)
      .map((key) => teeTimesPerSpace[key])
      .flat()

    if (allTimes?.length) {
      return allTimes.sort((a, b) => b.from_unix - a.from_unix)[0]
    } else {
      return null
    }
  }, [teeTimesPerSpace])

  const teeTimesOfFirstSpace = useMemo(() => {
    const firstKey = spaces?.[0]?.id
    return teeTimesPerSpace[firstKey]
  }, [teeTimesPerSpace, spaces])

  const intervalMinutes = useMemo(() => {
    if (earliestTeeTime) {
      const { from, to } = earliestTeeTime
      return Math.floor(Math.abs(new Date(from) - new Date(to)) / 1000 / 60)
    }
    return null
  }, [earliestTeeTime])

  const slotsPerHour = useMemo(() => {
    if (intervalMinutes) return 60 / intervalMinutes
    return 1
  }, [intervalMinutes])

  const venueTimezone = useMemo(() => {
    return currentVenue?.timezone
  }, [currentVenue])

  const numberOfHoursToShow = useMemo(() => {
    if (earliestTeeTime && latestTeeTime && venueTimezone) {
      const from = moment.unix(earliestTeeTime.from_unix)
      const to = moment.unix(latestTeeTime.to_unix)
      const duration = moment.duration(to.diff(from))
      return duration.asHours()
    }
    return null
  }, [earliestTeeTime, latestTeeTime, venueTimezone])

  const updateHorizontalTimeline = useCallback(() => {
    if (teeTimesOfFirstSpace && earliestTeeTime && latestTeeTime && intervalMinutes) {
      let topPosition = HEADER_HEIGHT
      let lastAdded = false

      const now = moment()
      const earliestTeeTimeDate = moment.unix(earliestTeeTime.from_unix)
      const latestTeeTimeDate = moment.unix(latestTeeTime.to_unix)

      if (
        now.format('YYYY-MM-DD') !== earliestTeeTimeDate.format('YYYY-MM-DD') ||
        now.isBefore(earliestTeeTimeDate) ||
        now.isAfter(latestTeeTimeDate)
      ) {
        setHorizontalTimelinePosition(null)
        return
      }

      teeTimesOfFirstSpace.forEach((teeTime) => {
        if (teeTime.to_unix <= now.unix()) topPosition += SLOT_HEIGHT
        if (teeTime.to_unix > now.unix() && !lastAdded) {
          const heightPerMinute = SLOT_HEIGHT / intervalMinutes

          const diff = moment.duration(moment.unix(teeTime.to_unix).diff(now)).asMinutes()
          topPosition += (intervalMinutes - diff) * heightPerMinute
          lastAdded = true
        }
      })

      setHorizontalTimelinePosition(topPosition)
    }

    return
  }, [teeTimesOfFirstSpace, earliestTeeTime, latestTeeTime, intervalMinutes])

  useEffect(() => {
    updateHorizontalTimeline()

    const interval = setInterval(() => updateHorizontalTimeline(), 60000)
    return () => {
      clearInterval(interval)
    }
  }, [updateHorizontalTimeline])

  const teeTimeIsFullHour = (teeTime) => {
    return moment.unix(teeTime.from_unix).minutes() === 0
  }

  const getTeeTimeFromHourString = (teeTime) => {
    return moment.unix(teeTime.from_unix).tz(venueTimezone).format('HH:mm')
  }

  if (
    !spaces ||
    !teeTimesOfFirstSpace ||
    !numberOfHoursToShow ||
    !slotsPerHour ||
    !teeTimesPerSpace
  ) {
    return null
  }

  return (
    <div
      className={cn(
        'relative flex flex-nowrap items-start justify-start overflow-scroll pb-[50px]',
        className
      )}
    >
      <div
        className={cn(
          'border-border-stroke-clean bottom-0 left-0 top-0 z-[50] flex h-auto w-[30px] flex-shrink-0 flex-grow-0 flex-col flex-nowrap items-end justify-start border-r border-solid bg-white lg:w-[100px]'
        )}
        style={{ marginTop: HEADER_HEIGHT }}
      >
        {teeTimesOfFirstSpace &&
          numberOfHoursToShow &&
          teeTimesOfFirstSpace.map((teeTime) => (
            <div
              key={teeTime.id}
              className={cn(
                '-mr-3 box-border flex items-start justify-end border-b border-solid border-white/0 pr-0 lg:z-auto lg:pr-5'
              )}
              style={{ height: SLOT_HEIGHT }}
            >
              {(teeTimeIsFullHour(teeTime) ||
                numberOfHoursToShow < 1 ||
                intervalMinutes === 60) && (
                <p
                  className={cn(
                    'font-content z-20 m-0 -translate-y-[13px] rounded-sm bg-white p-1 text-right text-sm font-medium text-black lg:-translate-y-[10px] lg:bg-transparent lg:p-0'
                  )}
                >
                  {getTeeTimeFromHourString(teeTime)}
                </p>
              )}
            </div>
          ))}
      </div>
      {render(SLOT_WIDTH, SLOT_HEIGHT, slotsPerHour, intervalMinutes, earliestTeeTime)}

      {horizontalTimelinePosition && (
        <div
          className={cn('absolute left-[30px] z-[5] h-[1px] bg-red-700/60 md:left-[100px]')}
          style={{ width: spaces?.length * SLOT_WIDTH, top: horizontalTimelinePosition }}
        >
          <div
            className={cn(
              'absolute -left-[4px] -top-[3px] h-[7px] w-[7px] rounded-full bg-red-700/60'
            )}
          ></div>
        </div>
      )}
    </div>
  )
}

SlotsCalendar.propTypes = {
  className: PropTypes.string,
  spaces: PropTypes.array,
  teeTimesPerSpace: PropTypes.object,
  currentVenue: PropTypes.object,
  render: PropTypes.func,
}

SlotsCalendar.defaultProps = {
  className: '',
  spaces: [],
  teeTimesPerSpace: {},
  currentVenue: {},
  render: () => {},
}

export default SlotsCalendar
